import parse from 'date-fns/parse'
import format from 'date-fns/format'
import addDays from 'date-fns/add_days'
import isSameDay from 'date-fns/is_same_day'
import getDaysInMonth from 'date-fns/get_days_in_month'

/**
 * Given an array of events, returns an array of days with events grouped by date.
 *
 * @export
 * @param {Array} events - Array of events (should already be expanded if needed)
 * @param {number} year - Target year (used when showAll is false)
 * @param {number} month - Target month (zero-indexed, used when showAll is false)
 * @param {boolean} showAll - If true, returns only days with events (useful for agenda views)
 * @returns {Array} Array of day objects { date, events, ... }
 */
export default function mapEventsToDays(
  events,
  year = 0,
  month = 0,
  showAll = false
) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  let days = []

  if (showAll) {
    // Collect unique event dates
    const uniqueDatesMap = new Map()

    events.forEach(event => {
      const eventDate = parse(event.start)
      const dateKey = format(eventDate, 'YYYY-MM-DD')
      if (!uniqueDatesMap.has(dateKey)) {
        uniqueDatesMap.set(dateKey, eventDate)
      }
    })

    days = Array.from(uniqueDatesMap.values()).map(date => createDayObject(date, events, false, today))
  } else {
    // Month view: create a full calendar grid
    const firstDayOfMonth = new Date(year, month, 1)
    const lastDayOfMonth = new Date(year, month + 1, 0)
    const daysInMonth = getDaysInMonth(firstDayOfMonth)

    const prevMonthDays = firstDayOfMonth.getDay() // How many days to show before the 1st of the month (0=Sunday)
    const nextMonthDays = 6 - lastDayOfMonth.getDay() // How many days to show after the last day of the month
    const totalDays = daysInMonth + prevMonthDays + nextMonthDays

    for (let i = -prevMonthDays; i < totalDays - prevMonthDays; i++) {
      const date = addDays(firstDayOfMonth, i)
      const outsideOfCurrentMonth = i < 0 || i >= daysInMonth
      days.push(createDayObject(date, events, outsideOfCurrentMonth, today))
    }
  }

  // Sort by unix timestamp (probably unnecessary unless something weird happens)
  return days.sort((a, b) => a.unix - b.unix)
}

/**
 * Builds a day object with events attached.
 *
 * @param {Date} date - The date this day represents.
 * @param {Array} events - The full events list.
 * @param {boolean} outsideOfCurrentMonth - Whether this date is outside the target month.
 * @param {Date} today - Today's date (normalized to 00:00).
 * @returns {Object} The day object.
 */
function createDayObject(date, events, outsideOfCurrentMonth = false, today) {
  const normalizedDate = new Date(date)
  normalizedDate.setHours(0, 0, 0, 0)

  return {
    outsideOfCurrentMonth,
    date: normalizedDate,
    unix: normalizedDate.getTime(),
    today: isSameDay(normalizedDate, today),
    events: events.filter(event => isSameDay(parse(event.start), normalizedDate)),
    weekday: format(normalizedDate, 'dddd'),
    day: format(normalizedDate, 'DD'),
    shortDay: format(normalizedDate, 'D'),
    number: format(normalizedDate, 'D'),
    month: format(normalizedDate, 'MMMM'),
    year: format(normalizedDate, 'YYYY'),
  }
}
